import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
} from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import {
  getStatsPage,
  type StatsListQuery,
  type StatsPage,
} from '../../../../services/competition/getStatsPage'
import { COMPETITION_STATS_STALE_TIME } from '../../constants'
import { flattenStatsPages, getNextStatsPage } from '../utils/statsPagination'

/** Module-level so React Query keeps the flattened list between renders. */
const selectRows = <Row>(data: InfiniteData<StatsPage<Row>, number>): Row[] =>
  flattenStatsPages(data.pages)

interface UseStatsListArgs {
  /** The stats endpoint path the tab's filters picked. */
  path: string
  query: StatsListQuery
  /** The page's Refresh/reprocess counter, if it has one — a bump refetches. */
  refreshKey?: unknown
  /** False until the tab has its filters and default sort. */
  enabled: boolean
  /**
   * Keep the previous list on screen while a new filter combination loads.
   * Off by default — see below.
   */
  keepPrevious?: boolean
}

/**
 * A paged player stats list for the Statistics tabs, cached per path + filter
 * set so returning to a tab, or to a filter combination already seen, renders
 * at once. Pages load on demand through `fetchNextPage` (infinite scroll).
 *
 * By default there is no placeholder data on a key change: the Schedule
 * Statistics tab swaps its column set with skater ↔ goalie, so an uncached
 * combination shows the skeleton instead of the previous list. Team Statistics
 * always kept its previous rows while reloading and opts in via `keepPrevious`.
 */
export const useStatsList = <Row = unknown>({
  path,
  query,
  refreshKey,
  enabled,
  keepPrevious = false,
}: UseStatsListArgs) => {
  const result = useInfiniteQuery({
    queryKey: queryKeys.statsList.byPath(path, query, refreshKey),
    queryFn: ({ pageParam, signal }) =>
      getStatsPage<Row>(path, query, pageParam, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      getNextStatsPage(lastPage, lastPageParam),
    enabled,
    staleTime: COMPETITION_STATS_STALE_TIME,
    select: selectRows<Row>,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  })

  return {
    /** `undefined` until the first page lands — the skeleton's cue. */
    items: result.data,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    refetch: result.refetch,
    error: result.error,
  }
}
